import { useEffect, useState } from "react";
import type { QuoteTier, SystemSummaryItem } from "@/api/types";
import ComponentLightbox from "@/components/quote/ComponentLightbox";
import LimeSpinner from "@/components/quote/LimeSpinner";
import SolarVisualization from "@/components/quote/SolarVisualization";
import type { MeshGroundFrame } from "@/utils/satelliteGroundPlane";
import type { RoofMeshLayout } from "@/utils/roofMeshPanelPlacement";
import type { SatellitePanelSnapshot } from "@/utils/satellitePanelOverlay";
import { adjacentPanelCount, bandFor, formatGbp, scorePillars, type PillarDisplay } from "@/utils/planViewModel";
import type { HelioScoreResult, SolarBuildingInsightsResult } from "@/api/types";
import ScoreBreakdown, { BigRing, MiniRing } from "@/components/plan/ScoreBreakdown";
import BatterySizePicker from "@/components/powlo/home/BatterySizePicker";

function Chevron({ open }: { open: boolean }) {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? "rotate(180deg)" : "none" }}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export default function SystemPlanCard({
  option,
  greetingName,
  addressLine,
  customised,
  panelCounts,
  batteryChoices,
  onPanels,
  onBattery,
  onReset,
  includesEv,
  onEv,
  heatPump,
  onHeatPump,
  score,
  scoreLoading,
  epcRating,
  upliftGbp,
  solarBuilding,
  satelliteImageUrl,
  mapCoordinates,
  satelliteZoom,
  satelliteImageSize,
  mesh3dUrl,
  meshGroundFrame,
  onLayout,
  onFittedCount,
  onSatellitePlacement,
  onClearCache,
  clearingCache,
  roofMediaPending = false,
  layoutRevision = 0,
  loading = false,
  loadingLabel = "Building your system…",
}: {
  option: QuoteTier | null;
  greetingName: string | null;
  addressLine: string | null;
  customised: boolean;
  panelCounts: number[];
  batteryChoices: number[];
  onPanels: (panels: number) => void;
  onBattery: (kwh: number) => void;
  onReset: () => void;
  includesEv: boolean;
  onEv: (value: boolean) => void;
  heatPump: boolean;
  onHeatPump: (value: boolean) => void;
  score: HelioScoreResult | null | undefined;
  scoreLoading: boolean;
  epcRating: string | null;
  upliftGbp: number | null;
  solarBuilding: SolarBuildingInsightsResult | null;
  satelliteImageUrl: string | null;
  mapCoordinates: { latitude: number; longitude: number } | null;
  satelliteZoom: number | null;
  satelliteImageSize: string | null;
  mesh3dUrl: string | null;
  meshGroundFrame: MeshGroundFrame | null;
  onLayout: (layout: RoofMeshLayout) => void;
  onFittedCount?: (count: number) => void;
  onSatellitePlacement?: (snapshot: SatellitePanelSnapshot | null) => void;
  onClearCache?: () => void;
  clearingCache?: boolean;
  roofMediaPending?: boolean;
  layoutRevision?: number;
  loading?: boolean;
  loadingLabel?: string;
}) {
  const [scoreOpen, setScoreOpen] = useState(false);
  const [lightboxItem, setLightboxItem] = useState<SystemSummaryItem | null>(null);
  // Modules still on the roof after a 3D edit. Price snaps to the closest catalog size.
  const [layoutPanelCount, setLayoutPanelCount] = useState<number | null>(null);
  const pillars: PillarDisplay[] = scorePillars(score);
  const overall = score?.overall_score;
  const band = overall != null ? bandFor(overall) : null;
  const panelTotal = layoutPanelCount ?? option?.panels ?? null;
  const fewerPanels = panelTotal != null ? adjacentPanelCount(panelCounts, panelTotal, "down") : null;
  const morePanels = panelTotal != null ? adjacentPanelCount(panelCounts, panelTotal, "up") : null;

  useEffect(() => {
    setLayoutPanelCount(null);
  }, [mesh3dUrl]);

  const choosePackagePanels = (count: number) => {
    setLayoutPanelCount(null);
    onPanels(count);
  };
  const annual = option ? (option.annualBenefitY1 ?? option.annualSavings) : null;
  const googleSolar = solarBuilding?.google_solar;

  return (
    <div className="powlo-dotted-indigo rounded-[32px] p-4 sm:p-8">
      <div className="overflow-hidden rounded-3xl bg-white">
        <div className="flex flex-col gap-3 border-b border-powlo-navy-light bg-powlo-navy px-6 py-4 text-white sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <div className="flex flex-col gap-1">
            <span className="font-powlo-display text-xl font-bold tracking-tight sm:text-2xl">Your Powlo Energy Plan</span>
            <span className="text-sm text-[#C9D1DE]">
              {greetingName ? <span className="font-bold text-powlo-lime">Hello {greetingName}</span> : "Your property"}
              {addressLine ? ` · ${addressLine}` : ""}
            </span>
          </div>
          {customised ? (
            <div className="flex items-center gap-2">
              <span className="rounded-full border border-white/15 bg-powlo-navy-light px-4 py-2 text-sm font-bold">Your setup</span>
              <button
                type="button"
                onClick={() => {
                  setLayoutPanelCount(null);
                  onReset();
                }}
                className="min-h-10 rounded-full bg-powlo-lime px-4 text-sm font-bold text-powlo-navy"
              >
                Reset to recommended
              </button>
            </div>
          ) : (
            <span className="w-fit rounded-full border border-white/15 bg-powlo-navy-light px-4 py-2 text-sm font-bold">
              Recommended setup
            </span>
          )}
        </div>

        {(scoreLoading || band) && (
          <div className="bg-powlo-navy text-white">
            <button
              type="button"
              onClick={() => setScoreOpen((open) => !open)}
              aria-expanded={scoreOpen}
              className="flex w-full items-center gap-4 px-6 py-5 text-left sm:px-8"
              disabled={overall == null}
            >
              {overall != null && band ? <MiniRing score={overall} color={band.color} /> : <span className="h-[62px] w-[62px]" />}
              <span className="flex-1">
                <span className="block font-powlo-display text-lg font-bold tracking-tight">Home Power Score</span>
                <span className="text-sm text-[#8FA0B8]">
                  {band ? <strong style={{ color: band.color }}>{band.label}</strong> : scoreLoading ? "Scoring this property…" : "Score appears once we have the property"}
                  {band ? " · six weighted pillars" : ""}
                </span>
              </span>
              {pillars.length > 0 && (
                <span className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-bold text-[#C9D1DE]">
                  {scoreOpen ? "Hide" : "Show breakdown"}
                  <Chevron open={scoreOpen} />
                </span>
              )}
            </button>
            {scoreOpen && overall != null && band && (
              <div className="grid grid-cols-1 gap-6 px-6 pb-8 sm:px-8 lg:grid-cols-[200px_1fr]">
                <div className="flex flex-col items-center gap-3 lg:items-start">
                  <span className="rounded-full border border-powlo-lime px-3 py-1 text-xs font-bold tracking-widest text-powlo-lime">
                    PERSONALISED
                  </span>
                  <BigRing score={overall} color={band.color} />
                </div>
                <ScoreBreakdown pillars={pillars} overall={overall} />
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col gap-6 p-6 sm:p-8">
          {!option ? (
            loading ? (
              <div className="flex flex-col items-center justify-center gap-3 py-16 text-powlo-muted" role="status">
                <span className="flex items-center justify-center rounded-full bg-white p-3">
                  <LimeSpinner size="lg" color="current" className="text-brand-navy" decorative />
                </span>
                {loadingLabel}
              </div>
            ) : (
              <p className="m-0 text-powlo-muted">We are sizing a system from your address and usage.</p>
            )
          ) : (
            <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
              <div className="contents lg:flex lg:min-w-0 lg:flex-col lg:gap-6">
                <div className="relative order-1 flex min-h-[220px] min-w-0 flex-col items-center justify-center gap-3 overflow-hidden rounded-2xl border border-powlo-border bg-powlo-cream p-4 text-center lg:order-none">
                  {googleSolar ? (
                    <div className="w-full text-left">
                      <SolarVisualization
                        insights={googleSolar}
                        propertyBoundary={solarBuilding?.property_boundary_geojson}
                        viableRoofPolygons={solarBuilding?.viable_roof_geojson}
                        satelliteImageUrl={satelliteImageUrl}
                        mapCenter={mapCoordinates}
                        mapZoom={satelliteZoom}
                        mapSize={satelliteImageSize}
                        inspireCoverage={solarBuilding?.inspire_coverage}
                        mesh3dUrl={mesh3dUrl}
                        groundFrame={meshGroundFrame}
                        onLayout={onLayout}
                        onFittedCount={onFittedCount}
                        visiblePanelCount={panelTotal}
                        onPlacedPanelCount={(count) => {
                          setLayoutPanelCount(count);
                          onPanels(count);
                        }}
                        onSatellitePlacement={onSatellitePlacement}
                        onClearCache={onClearCache}
                        clearingCache={clearingCache}
                        meshPending={roofMediaPending}
                        layoutRevision={layoutRevision}
                      />
                    </div>
                  ) : (
                    <>
                      <div className="font-powlo-display text-lg font-bold">
                        {panelTotal != null ? `${panelTotal} panels · ` : ""}
                        {option.systemKw} kWp
                        {option.batteryKwh > 0 ? ` · ${option.batteryKwh} kWh battery` : ""}
                      </div>
                      {roofMediaPending && <LimeSpinner size="md" color="current" className="text-powlo-navy" />}
                    </>
                  )}
                  {googleSolar && (
                    <>
                      <div className="font-powlo-display text-lg font-bold">
                        {panelTotal != null ? `${panelTotal} panels · ` : ""}
                        {option.systemKw} kWp
                        {option.batteryKwh > 0 ? ` + ${option.batteryKwh} kWh battery` : ""}
                      </div>
                      {roofMediaPending && <LimeSpinner size="md" color="current" className="text-powlo-navy" />}
                    </>
                  )}
                </div>
                <div className="order-3 flex flex-col gap-4 lg:order-none">
                  {option.description && (
                    <div className="rounded-2xl bg-[#E9E7FF] p-4 text-sm leading-relaxed">
                      <strong>Recommended: </strong>
                      {option.description}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    <Stat label="Annual benefit" value={annual != null ? formatGbp(annual) : "—"} dark />
                    <Stat label="Payback" value={Number.isFinite(option.paybackYears) ? `${option.paybackYears} yrs` : "—"} />
                    <Stat label="Independence" value={`${option.independencePct}%`} />
                  </div>
                </div>
              </div>
              <div className="order-2 flex min-w-0 flex-col gap-5 rounded-2xl border border-powlo-border p-5 lg:order-none">
                <div className="font-powlo-display text-lg font-bold tracking-tight">Customise your system</div>
                {panelCounts.length > 1 && panelTotal != null && (
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-bold">Solar panels</span>
                    <div className="flex items-center gap-2 rounded-2xl bg-[#F1F0EC] p-1">
                      <button
                        type="button"
                        aria-label="Fewer panels"
                        disabled={fewerPanels == null}
                        onClick={() => fewerPanels != null && choosePackagePanels(fewerPanels)}
                        className="h-10 w-10 rounded-xl bg-white text-xl font-bold disabled:opacity-40"
                      >
                        −
                      </button>
                      <div className="w-10 text-center font-powlo-display text-xl font-bold">{panelTotal}</div>
                      <button
                        type="button"
                        aria-label="More panels"
                        disabled={morePanels == null}
                        onClick={() => morePanels != null && choosePackagePanels(morePanels)}
                        className="h-10 w-10 rounded-xl bg-white text-xl font-bold disabled:opacity-40"
                      >
                        +
                      </button>
                    </div>
                  </div>
                )}
                <BatterySizePicker compact choices={batteryChoices} value={option.batteryKwh} onChange={onBattery} />
                <div className="flex flex-col gap-1 border-t border-[#ECEAE3] pt-3">
                  <span className="pb-1 font-bold">Your future plans</span>
                  <ToggleRow
                    label="Planning to get an EV"
                    sub="Saved with your quote. It does not change this price."
                    checked={includesEv}
                    onChange={onEv}
                  />
                  <ToggleRow
                    label="Planning a heat pump"
                    sub="Noted for you. It does not change this price or score."
                    checked={heatPump}
                    onChange={onHeatPump}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <MiniStat label="System price" value={formatGbp(option.price)} />
                  <MiniStat label="CO₂ offset" value={`${option.co2Tonnes}t / yr`} />
                </div>
                {(epcRating || upliftGbp != null) && (
                  <div className="grid grid-cols-2 gap-2">
                    {epcRating && (
                      <div className="rounded-xl border border-powlo-border p-4">
                        <div className="font-bold">EPC rating</div>
                        <div className="font-powlo-display text-2xl font-bold">{epcRating}</div>
                      </div>
                    )}
                    {upliftGbp != null && (
                      <div className="rounded-xl border border-powlo-border p-4">
                        <div className="font-bold">Home value uplift</div>
                        <div className="font-powlo-display text-xl font-bold text-green-700">+{formatGbp(upliftGbp)}</div>
                      </div>
                    )}
                  </div>
                )}
                {option.systemSummaryItems && option.systemSummaryItems.length > 0 && (
                  <ul className="m-0 flex list-none flex-col gap-2 p-0 text-sm">
                    {option.systemSummaryItems.map((item) => (
                      <li key={`${item.key}-${item.label}`}>
                        <button type="button" className="font-semibold text-powlo-indigo" onClick={() => setLightboxItem(item)}>
                          {item.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="border-t border-powlo-border px-6 py-4 text-xs text-powlo-muted-light sm:px-8">
          Prices, savings and the score come from your quote. Changing panels or battery picks the closest system we can price.
        </div>
      </div>
      <ComponentLightbox
        item={lightboxItem}
        open={lightboxItem !== null}
        onOpenChange={(open) => {
          if (!open) setLightboxItem(null);
        }}
      />
    </div>
  );
}

function ToggleRow({
  label,
  sub,
  checked,
  onChange,
}: {
  label: string;
  sub: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <button type="button" onClick={() => onChange(!checked)} className="flex min-h-[52px] items-center justify-between gap-4 text-left">
      <span>
        <span className="block font-semibold">{label}</span>
        <span className="text-xs text-powlo-muted">{sub}</span>
      </span>
      <span className={`flex h-7 w-[50px] shrink-0 items-center rounded-full p-[3px] transition-colors ${checked ? "bg-powlo-indigo" : "bg-[#D4D2CB]"}`}>
        <span className={`block h-5 w-5 rounded-full shadow-sm transition-transform ${checked ? "translate-x-[22px] bg-white" : "translate-x-0 bg-powlo-ink"}`} />
      </span>
    </button>
  );
}

function Stat({ label, value, dark }: { label: string; value: string; dark?: boolean }) {
  return (
    <div className={`rounded-2xl p-4 ${dark ? "bg-powlo-navy text-white" : "border border-powlo-border"}`}>
      <div className={`text-xs ${dark ? "text-[#C9D1DE]" : "text-powlo-muted"}`}>{label}</div>
      <div className={`font-powlo-display text-2xl font-bold tracking-tight ${dark ? "text-powlo-lime" : ""}`}>{value}</div>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-[#ECEAE3] bg-[#FCFBF8] p-3">
      <div className="text-xs text-powlo-muted">{label}</div>
      <div className="font-powlo-display text-lg font-bold">{value}</div>
    </div>
  );
}
